import React, { ReactNode, useState } from 'react';
import { Skeleton } from '@mui/material';
import { Markup } from 'interweave';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { addPointsInText } from '../../../../helpers/app';
import { images } from '../../../../constants/images';
import { PublicationMobileContentProps } from '../../interfaces';
import styles from '../../publicationDetailMobile.module.scss';
import InformationLoader from '../../../PublicationDetail/Loader/Information/InformationLoader';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { usePublicationRecords } from '../../../PublicationDetail/hooks/usePublicationRecords';
import StarIcon from '@mui/icons-material/Star';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

const PublicationMobileContent: React.FC<PublicationMobileContentProps> = ({
    publication,
    loading,
    logoDealerships,
    handleDealerLogoClick,
    brand,
    type,
    vehicleTypeSelected,
    selectedVersion,
    addPointsInText,
    truncateText,
    boldBetweenAsterisks,
    hasValidSuggestedPricesPublication,
    getPriceCategoryPublication,
    getIndicatorPositionPublication,
    getPriceDifferenceMessage,
    loginBancoChile,
    children,
}) => {
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const { recordsToShow } = usePublicationRecords({ publication, type });

    const toggleDetails = () => {
        setIsDetailsOpen(!isDetailsOpen);
    };
    if (loading && !publication?.id_publication) {
        return (
            <div className={`${styles.information} ${loading && styles.loader}`}>
                <InformationLoader />
            </div>
        );
    }

    const renderPriceIndicator = () => {
        if (publication?.opportunity === true) {
            return (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <StarIcon
                        className={styles.priceIcon}
                        style={{ width: '16px', height: '16px', marginRight: '4px', color: '#7100FF' }}
                    />
                    <p style={{ padding: '0', margin: '0', marginTop: '2px',color: '#000000' }}>Oportunidad</p>
                </div>
            );
        }


        if (!hasValidSuggestedPricesPublication) {
            return null;
        }

        const className = type === 'myPublication'
            ? styles.priceIndicatorMyPublication
            : styles.priceIndicatorPublication;

        if (getPriceCategoryPublication() === "good" || getPriceCategoryPublication() === "fair") {
            return (
                <div className={className} style={{ display: 'flex', alignItems: 'center' }}>
                    <img
                        src={
                            getPriceCategoryPublication() === "good"
                                ? images.checkVerde
                                : images.checkAmarillo
                        }
                        alt="Indicador de precio"
                        title="Indicador de precio"
                        className={styles.priceIcon}
                        style={{ width: '16px', height: '16px', marginRight: '4px' }}
                    />
                    {getPriceCategoryPublication() === "good"
                        ? "Buen precio"
                        : "Precio justo"}
                </div>
            );
        }

        return null;
    };

    const getVehicleInfo = () => {
        if (brand) {
            const selectedVehicle = publication?.vehicles?.find((v: any) => v._id === selectedVersion);
            return {
                year: selectedVehicle?.year || publication?.vehicle_data?.year || publication?.primary_description,
                mileage: selectedVehicle?.mileage || publication?.vehicle_data?.mileage || publication?.secondary_description
            };
        }
        return {
            year: publication?.vehicle_data?.year || publication?.primary_description,
            mileage: publication?.vehicle_data?.mileage || publication?.secondary_description
        };
    };

    const { year, mileage } = getVehicleInfo();

    const isDefaultRecord = (item: any): boolean => {
        const defaultTexts = [
            'Sin datos de uso comercial o personal',
            'Sin informacion de propietarios',
            'Sin datos sobre accidentes reportados',
        ];
        if (defaultTexts.includes(item?.value)) return true;
        return false;
    };

    const getChipLabel = (item: any): string => {
        switch (item.key) {
            case 'personal_use': {
                if (item.value.toLowerCase().includes('personal')) return 'Uso: Particular';
                if (item.value.toLowerCase().includes('comercial')) return 'Uso: Comercial';
                return 'Uso: Particular';
            }
            case 'sole_owner': {
                const num = item.value.match(/\d+/);
                if (num) return `Dueños: ${num[0]}`;
                if (item.value.toLowerCase().includes('único') || item.value.toLowerCase().includes('unico')) return 'Dueños: 1';
                if (item.value.toLowerCase().includes('multiple') || item.value.toLowerCase().includes('múltiple')) return 'Dueños: 2+';
                return 'Dueños: 1';
            }
            case 'accidents_reported': {
                if (item.value.toLowerCase().includes('con')) return 'Con Accidentes';
                return 'Sin Accidentes';
            }
            case 'negotiate': {
                if (item.value.toLowerCase().includes('no está') || item.value.toLowerCase().includes('no esta')) return 'Precio fijo';
                return 'Negociable';
            }
            default:
                return item.value;
        }
    };

    const renderRecordChips = () => {
        if (loading) return null;

        const hasAnyData = recordsToShow.some((item: any) => !isDefaultRecord(item));
        const allEmpty = recordsToShow.every((item: any) => isDefaultRecord(item));

        if (allEmpty) {
            return (
                <div className={styles.sellerInfoChips}>
                    <div className={styles.chipEmpty}>
                        <img src={recordsToShow[0]?.icon} alt="Sin registros previos" className={styles.chipIcon} />
                        <span>Sin registros previos</span>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.sellerInfoChips}>
                {recordsToShow.map((item: any, index: number) => (
                    <div
                        key={index}
                        className={isDefaultRecord(item) ? styles.chipEmpty : styles.chipFilled}
                    >
                        <img src={item?.icon} alt={item?.name || item?.key} className={styles.chipIcon} />
                        <span>{getChipLabel(item)}</span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className={styles.information}>
            {logoDealerships && logoDealerships !== undefined && logoDealerships !== null && (
                <div className={brand ? styles.brandLogo : styles.dealerLogo} onClick={handleDealerLogoClick}>
                    <img src={logoDealerships} alt="Dealer logo" title="Dealer logo" />
                </div>
            )}

            <p className={styles.description}>
                {year} | {addPointsInText(mileage)} km
            </p>

            <h2 className={styles.title}>{publication?.title}</h2>

            {(publication?.vehicle_data?.version || publication?.version) && (
                <h3 className={styles.version}>{publication?.vehicle_data?.version || publication?.version}</h3>
            )}

            {brand ? (
                <>
                    <p className={styles.pricesBrand}>Desde ${addPointsInText(publication?.price)} (*)</p>
                    <p className={styles.subTextPricesBrand}>(*) Precios referenciales</p>
                </>
            ) : (
                <>
                    <div className={styles.priceContainer}>
                        <div className={styles.priceIvaGroup}>
                            <p className={styles.price}>$ {addPointsInText(publication?.price)}</p>
                            {publication?.detail_edit?.iva && vehicleTypeSelected && <p className={styles.indicatorIVA}>+ IVA</p>}
                        </div>
                        {publication?.status_brief?.code !== "sold_out" && (type === 'myPublication' || type === 'publication' || type === 'salePost') && renderPriceIndicator()}
                    </div>
                </>
            )}
            {!brand && (
                <div className={styles.comparaContainer}>
                    <p className={styles.region}>
                        <LocationOnOutlinedIcon className={styles.locationIcon} />
                        {truncateText(publication?.region, 36)}
                    </p>
                </div>
            )}

            {/* Record chips */}
            {!brand && renderRecordChips()}

            {/* Render children for myPublication type */}
            {children}
        </div>
    );
};

export default PublicationMobileContent;
